import { useNavigate, useParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import T, { useTranslation } from '@wojtekmaj/react-t';
import { BadgeCheckIcon } from 'lucide-react';

import {
  Amount,
  ButtonAndRoadblockWrapper,
  ButtonWrapper,
  CheckWrapper,
  ContentWrapper,
  Description,
  Footer,
  Hint,
  Icon,
  Image,
  ImagePlaceholder,
  ImageWrapper,
  Name,
  Price,
  ScrollContainer,
  Wrapper,
} from './index.styles';

import EmailVerificationRoadblock from '../../../../../../common/email_verification_roadblock';
import HeaderWithButton from '../../../../header_with_button';
import RedeemButton from './redeem_button';

import useI18nText from '../../../../../../../hooks/useI18nText';

import { formatNumber } from '../../../../../../../utils/helpers';

import {
  currentUserState,
  transactionsQuery,
  vendorCardRewardQuery,
  vendorCardSubscriptionState,
} from '../../../../../../../store';

import lajkonikIcon from '../../../../../../../static/lajkonik.svg';
import rewardIcon from '../../../../../../../static/reward.svg';

import type { Reward, Transaction } from '@rewardopl/types';

export default function RewardPage() {
  const navigate = useNavigate();
  const { reward_id: rewardId } = useParams<{ reward_id?: Reward['_id'] }>();

  if (!rewardId) {
    throw new Error('rewardId is undefined');
  }

  const cardSubscription = useAtomValue(vendorCardSubscriptionState);
  const currentUser = useAtomValue(currentUserState);

  const { _id: cardSubscriptionId, balance } = cardSubscription;

  const { is_email_verified: isEmailVerified = true } = currentUser;

  const setTransactions = useSetAtom(transactionsQuery(cardSubscriptionId));
  const reward = useAtomValue(vendorCardRewardQuery(rewardId));
  const lajkoniksLabel = useTranslation('lajkoniks');

  const {
    description,
    description_i18n: descriptionI18n,
    name,
    name_i18n: nameI18n,
    picture,
    price,
  } = reward;

  const nameString = useI18nText(nameI18n, name);
  const descriptionString = useI18nText(descriptionI18n, description ?? '');

  async function onSuccess(nextTransaction: Transaction) {
    setTimeout(() => {
      navigate(`../../reward_received/${nextTransaction._id}`);
    }, 0);
  }

  const isAvailable = balance >= price;

  return (
    <Wrapper>
      <ScrollContainer>
        <HeaderWithButton>Reward</HeaderWithButton>
        <ContentWrapper>
          <ImageWrapper>
            {picture ? (
              <Image alt={nameString} src={`/api/uploads/${picture}?w=${70 * devicePixelRatio}`} />
            ) : (
              <ImagePlaceholder alt={nameString} src={rewardIcon} />
            )}
            {isAvailable ? (
              <CheckWrapper>
                <BadgeCheckIcon />
              </CheckWrapper>
            ) : null}
          </ImageWrapper>
          <Name>{nameString}</Name>
          <Price>
            <Amount>{formatNumber(price)} × </Amount>
            <Icon alt={lajkoniksLabel} src={lajkonikIcon} />
          </Price>
          {descriptionString ? <Description>{descriptionString}</Description> : null}
          <Hint>
            {isAvailable ? (
              <T price={price}>
                {
                  'After clicking “Redeem” you’ll receive reward, and {price} lajkoniks will be taken from your card.'
                }
              </T>
            ) : (
              <T>You don’t have enough lajkoniks to get this reward.</T>
            )}
          </Hint>
        </ContentWrapper>
      </ScrollContainer>
      <Footer>
        <ButtonWrapper>
          <ButtonAndRoadblockWrapper>
            <RedeemButton
              disabled={!isAvailable || !isEmailVerified}
              onSuccess={onSuccess}
              reward={reward}
              setTransactions={setTransactions}
            />
            {isEmailVerified ? null : <EmailVerificationRoadblock />}
          </ButtonAndRoadblockWrapper>
        </ButtonWrapper>
      </Footer>
    </Wrapper>
  );
}
